import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { api } from '@/api/client';
import { toast } from '@/state/toast';
import type { Remark } from '@/types/api';

interface Props {
  projectId: string;
  remark: Remark;
  /** Какое поле корректируется. UI пилотно ограничен этим набором (соответствует backend whitelist). */
  field: 'section_code' | 'aspect' | 'defect_type' | 'severity' | 'expert_name';
  onClose: () => void;
}

const FIELD_LABELS: Record<Props['field'], string> = {
  section_code: 'Раздел',
  aspect:       'Аспект',
  defect_type:  'Тип дефекта',
  severity:     'Важность',
  expert_name:  'Эксперт',
};

/** Модалка коррекции поля LLM-извлечения куратором (red-item).
 *  reason — свободный текст (≥3 символа), не select. */
export function CorrectRemarkModal({ projectId, remark, field, onClose }: Props) {
  const currentValue = (remark as unknown as Record<string, unknown>)[field];
  const [newValue, setNewValue] = useState<string>(currentValue == null ? '' : String(currentValue));
  const [reason, setReason] = useState('');
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => api.post(
      `/projects/${projectId}/remarks/${remark.id}/correct`,
      {
        field,
        new_value: newValue.trim() || null,
        reason: reason.trim(),
      },
    ),
    onSuccess: () => {
      toast.success('Коррекция сохранена');
      qc.invalidateQueries({ queryKey: ['remarks', projectId] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось сохранить'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (reason.trim().length < 3) {
      toast.error('Причина: минимум 3 символа');
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal
      title={`Исправить: ${FIELD_LABELS[field]}`}
      onClose={onClose}
      maxWidth={520}
      footer={
        <div className="flex justify-end" style={{ gap: 8 }}>
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            onClick={() => submit(new Event('submit') as unknown as FormEvent)}
            disabled={mutation.isPending || reason.trim().length < 3}
          >
            {mutation.isPending ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </div>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label className="text-muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Новое значение
          </label>
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder={`Например: ${currentValue || '—'}`}
            className="bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
            style={{ padding: '10px 12px', fontSize: 14, width: '100%', marginTop: 6 }}
            autoFocus
          />
          <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
            Текущее: <span className="tabular">{String(currentValue || '—')}</span>
          </div>
        </div>

        <div>
          <label className="text-muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Причина коррекции <span style={{ color: '#EF4444' }}>*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Опишите, почему исправляете (≥3 символа). Эта правка попадёт в golden-set для дообучения LLM."
            className="bg-white text-ink border border-border rounded-btn outline-none focus:border-ink"
            style={{ padding: '10px 12px', fontSize: 13, width: '100%', minHeight: 80, marginTop: 6, resize: 'vertical', fontFamily: 'inherit' }}
          />
          <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
            {reason.trim().length < 3 ? `Ещё ${3 - reason.trim().length} симв.` : `${reason.length} симв.`}
          </div>
        </div>
      </form>
    </Modal>
  );
}
